/**
 * Iter53 P46 — Backoffice tab extras din SaleForm.js (~314 LOC).
 *
 * Consumă state prin `useSaleFormContext()` — zero prop drilling.
 * Signature: <SaleFormBackofficeTab /> (fără props).
 *
 * 4 secțiuni:
 *   - Backoffice operațional (BO received, verified, uploaded, notes_bo)
 *   - Probleme & retur
 *   - Workflow Salesforce (supplier_status, business_status)
 *   - Quality Call & anulare
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { useSaleFormContext } from "./SaleFormContext";

export const SaleFormBackofficeTab = () => {
  const { formData, handleChange, canWrite, isHidden, isEdit } = useSaleFormContext();

  return (
    <>
            {!isEdit && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Aceste câmpuri se completează după salvarea vânzării (pagina de Editare).
              </div>
            )}

            {/* BO operațional */}
            <Card data-testid="bo-section-operational">
              <CardHeader>
                <CardTitle className="text-base">Backoffice operațional</CardTitle>
                <CardDescription>Status verificare, încărcare la furnizor și note BO.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isHidden("sale.bo_received_at") && (
                <div className="space-y-2">
                  <Label>Data primire BO (dd.mm.yyyy)</Label>
                  <Input value={formData.bo_received_at}
                    onChange={(e) => handleChange("bo_received_at", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("sale.bo_received_at")}
                    data-testid="bo_received_at" />
                </div>
                )}
                {!isHidden("sale.bo_verified") && (
                <div className="space-y-2 flex items-center gap-2 pt-6">
                  <Checkbox id="bo_verified_iter435" checked={formData.bo_verified}
                    onCheckedChange={(v) => handleChange("bo_verified", !!v)}
                    disabled={!isEdit || !canWrite("sale.bo_verified")}
                    data-testid="bo_verified_iter435" />
                  <Label htmlFor="bo_verified_iter435" className="cursor-pointer">Verificat BO</Label>
                </div>
                )}
                {!isHidden("sale.uploaded_flag") && (
                <div className="space-y-2 flex items-center gap-2">
                  <Checkbox id="uploaded_flag" checked={formData.uploaded_flag}
                    onCheckedChange={(v) => handleChange("uploaded_flag", !!v)}
                    disabled={!isEdit || !canWrite("sale.uploaded_flag")}
                    data-testid="uploaded_flag" />
                  <Label htmlFor="uploaded_flag" className="cursor-pointer">Încărcat la furnizor</Label>
                </div>
                )}
                {!isHidden("sale.uploaded_at") && (
                <div className="space-y-2">
                  <Label>Data încărcare furnizor</Label>
                  <Input value={formData.uploaded_at}
                    onChange={(e) => handleChange("uploaded_at", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("sale.uploaded_at")}
                    data-testid="uploaded_at" />
                </div>
                )}
                {!isHidden("sale.notes_bo") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Note BO</Label>
                  <Textarea rows={3} value={formData.notes_bo}
                    onChange={(e) => handleChange("notes_bo", e.target.value)}
                    disabled={!isEdit || !canWrite("sale.notes_bo")}
                    data-testid="notes_bo" />
                </div>
                )}
                <div className="space-y-2 md:col-span-2 rounded-md border border-slate-200 p-3 bg-slate-50">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Documente atașate (read-only — administrare separată)</Label>
                  <p className="text-sm text-slate-700" data-testid="documents">
                    Documentele se gestionează din pagina <strong>Detalii → Documente</strong>.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Probleme & retur */}
            <Card data-testid="bo-section-problems">
              <CardHeader>
                <CardTitle className="text-base">Probleme &amp; retur</CardTitle>
                <CardDescription>Marcaje pentru contracte cu probleme sau returnate.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isHidden("sale.problems") && (
                <div className="space-y-2 flex items-center gap-2">
                  <Checkbox id="problems" checked={formData.problems}
                    onCheckedChange={(v) => handleChange("problems", !!v)}
                    disabled={!isEdit || !canWrite("sale.problems")}
                    data-testid="problems" />
                  <Label htmlFor="problems" className="cursor-pointer">Probleme identificate</Label>
                </div>
                )}
                {!isHidden("sale.requires_rework") && (
                <div className="space-y-2 flex items-center gap-2">
                  <Checkbox id="requires_rework" checked={formData.requires_rework}
                    onCheckedChange={(v) => handleChange("requires_rework", !!v)}
                    disabled={!isEdit || !canWrite("sale.requires_rework")}
                    data-testid="requires_rework" />
                  <Label htmlFor="requires_rework" className="cursor-pointer">Necesită refacere</Label>
                </div>
                )}
                {!isHidden("sale.problems_description") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Descriere probleme</Label>
                  <Textarea rows={2} value={formData.problems_description}
                    onChange={(e) => handleChange("problems_description", e.target.value)}
                    disabled={!isEdit || !canWrite("sale.problems_description")}
                    data-testid="problems_description" />
                </div>
                )}
                {!isHidden("sale.problem_reason_code") && (
                <div className="space-y-2">
                  <Label>Cod motiv problemă</Label>
                  <Input value={formData.problem_reason_code}
                    onChange={(e) => handleChange("problem_reason_code", e.target.value)}
                    placeholder="ex: WRONG_POD, MISSING_DOC"
                    disabled={!isEdit || !canWrite("sale.problem_reason_code")}
                    data-testid="problem_reason_code" />
                </div>
                )}
                {!isHidden("sale.returned") && (
                <div className="space-y-2 flex items-center gap-2">
                  <Checkbox id="returned" checked={formData.returned}
                    onCheckedChange={(v) => handleChange("returned", !!v)}
                    disabled={!isEdit || !canWrite("sale.returned")}
                    data-testid="returned" />
                  <Label htmlFor="returned" className="cursor-pointer">Returnat</Label>
                </div>
                )}
                {!isHidden("sale.return_date") && (
                <div className="space-y-2">
                  <Label>Data retur</Label>
                  <Input value={formData.return_date}
                    onChange={(e) => handleChange("return_date", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("sale.return_date")}
                    data-testid="return_date" />
                </div>
                )}
                {!isHidden("sale.return_reason") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Motiv retur</Label>
                  <Textarea rows={2} value={formData.return_reason}
                    onChange={(e) => handleChange("return_reason", e.target.value)}
                    disabled={!isEdit || !canWrite("sale.return_reason")}
                    data-testid="return_reason" />
                </div>
                )}
              </CardContent>
            </Card>

            {/* Workflow SF — Supplier + ContractStatus */}
            <Card data-testid="bo-section-workflow-sf">
              <CardHeader>
                <CardTitle className="text-base">Workflow Salesforce</CardTitle>
                <CardDescription>Status furnizor, Quality Call și anulări sincronizate cu Salesforce.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isHidden("contract.business_status") && (
                <div className="space-y-2">
                  <Label>Business Status (SF)</Label>
                  <Input value={formData.business_status}
                    onChange={(e) => handleChange("business_status", e.target.value)}
                    placeholder="ex: active, cancelled, pending"
                    disabled={!isEdit || !canWrite("contract.business_status")}
                    data-testid="business_status" />
                </div>
                )}
                {!isHidden("contract_status.salesforce_contract_state") && (
                <div className="space-y-2">
                  <Label>Stare contract SF</Label>
                  <Input value={formData.salesforce_contract_state}
                    onChange={(e) => handleChange("salesforce_contract_state", e.target.value)}
                    disabled={!isEdit || !canWrite("contract_status.salesforce_contract_state")}
                    data-testid="salesforce_contract_state" />
                </div>
                )}
                {!isHidden("contract_status.salesforce_status_date") && (
                <div className="space-y-2">
                  <Label>Data stare SF</Label>
                  <Input value={formData.salesforce_status_date}
                    onChange={(e) => handleChange("salesforce_status_date", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("contract_status.salesforce_status_date")}
                    data-testid="salesforce_status_date" />
                </div>
                )}
                {!isHidden("contract.supplier_status") && (
                <div className="space-y-2">
                  <Label>Status furnizor</Label>
                  <Input value={formData.supplier_status}
                    onChange={(e) => handleChange("supplier_status", e.target.value)}
                    placeholder="ex: ok, ko, pending"
                    disabled={!isEdit || !canWrite("contract.supplier_status")}
                    data-testid="supplier_status" />
                </div>
                )}
                {!isHidden("contract.supplier_ok") && (
                <div className="space-y-2 flex items-center gap-2">
                  <Checkbox id="supplier_ok" checked={formData.supplier_ok}
                    onCheckedChange={(v) => handleChange("supplier_ok", !!v)}
                    disabled={!isEdit || !canWrite("contract.supplier_ok")}
                    data-testid="supplier_ok" />
                  <Label htmlFor="supplier_ok" className="cursor-pointer">Furnizor OK</Label>
                </div>
                )}
                {!isHidden("contract.supplier_ko") && (
                <div className="space-y-2 flex items-center gap-2">
                  <Checkbox id="supplier_ko" checked={formData.supplier_ko}
                    onCheckedChange={(v) => handleChange("supplier_ko", !!v)}
                    disabled={!isEdit || !canWrite("contract.supplier_ko")}
                    data-testid="supplier_ko" />
                  <Label htmlFor="supplier_ko" className="cursor-pointer">Furnizor KO</Label>
                </div>
                )}
                {!isHidden("contract.supplier_status_date") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Data status furnizor</Label>
                  <Input value={formData.supplier_status_date}
                    onChange={(e) => handleChange("supplier_status_date", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("contract.supplier_status_date")}
                    data-testid="supplier_status_date" />
                </div>
                )}
              </CardContent>
            </Card>

            {/* Quality Call */}
            <Card data-testid="bo-section-quality-call">
              <CardHeader>
                <CardTitle className="text-base">Quality Call &amp; anulare</CardTitle>
                <CardDescription>Detalii apel de calitate și motivare anulare.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isHidden("sale.qc_call_date") && (
                <div className="space-y-2">
                  <Label>Data Quality Call</Label>
                  <Input value={formData.qc_call_date}
                    onChange={(e) => handleChange("qc_call_date", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("sale.qc_call_date")}
                    data-testid="qc_call_date" />
                </div>
                )}
                {!isHidden("sale.qc_operator_name") && (
                <div className="space-y-2">
                  <Label>Operator Quality Call</Label>
                  <Input value={formData.qc_operator_name}
                    onChange={(e) => handleChange("qc_operator_name", e.target.value)}
                    disabled={!isEdit || !canWrite("sale.qc_operator_name")}
                    data-testid="qc_operator_name" />
                </div>
                )}
                {!isHidden("sale.quality_call_outcome") && (
                <div className="space-y-2">
                  <Label>Rezultat QC (sale-level)</Label>
                  <Input value={formData.quality_call_outcome}
                    onChange={(e) => handleChange("quality_call_outcome", e.target.value)}
                    placeholder="ok / nok"
                    disabled={!isEdit || !canWrite("sale.quality_call_outcome")}
                    data-testid="quality_call_outcome" />
                </div>
                )}
                {!isHidden("contract_status.quality_call_outcome") && (
                <div className="space-y-2">
                  <Label>Rezultat QC (contract-level)</Label>
                  <Input value={formData.cs_quality_call_outcome}
                    onChange={(e) => handleChange("cs_quality_call_outcome", e.target.value)}
                    disabled={!isEdit || !canWrite("contract_status.quality_call_outcome")}
                    data-testid="cs_quality_call_outcome" />
                </div>
                )}
                {!isHidden("contract_status.quality_call_date") && (
                <div className="space-y-2">
                  <Label>Data QC (contract-level)</Label>
                  <Input value={formData.cs_quality_call_date}
                    onChange={(e) => handleChange("cs_quality_call_date", e.target.value)}
                    placeholder="dd.mm.yyyy"
                    disabled={!isEdit || !canWrite("contract_status.quality_call_date")}
                    data-testid="cs_quality_call_date" />
                </div>
                )}
                {!isHidden("sale.rejection_subreason") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Subrază respingere</Label>
                  <Input value={formData.rejection_subreason}
                    onChange={(e) => handleChange("rejection_subreason", e.target.value)}
                    disabled={!isEdit || !canWrite("sale.rejection_subreason")}
                    data-testid="rejection_subreason" />
                </div>
                )}
                {!isHidden("sale.cancellation_reason_text") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Motiv anulare (text liber)</Label>
                  <Textarea rows={2} value={formData.cancellation_reason_text}
                    onChange={(e) => handleChange("cancellation_reason_text", e.target.value)}
                    disabled={!isEdit || !canWrite("sale.cancellation_reason_text")}
                    data-testid="cancellation_reason_text" />
                </div>
                )}
                {!isHidden("contract_status.cancellation_reason") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Motiv anulare (contract-level, codificat)</Label>
                  <Input value={formData.cs_cancellation_reason}
                    onChange={(e) => handleChange("cs_cancellation_reason", e.target.value)}
                    disabled={!isEdit || !canWrite("contract_status.cancellation_reason")}
                    data-testid="cs_cancellation_reason" />
                </div>
                )}
                {!isHidden("contract_status.cancellation_details") && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Detalii anulare (contract-level)</Label>
                  <Textarea rows={2} value={formData.cs_cancellation_details}
                    onChange={(e) => handleChange("cs_cancellation_details", e.target.value)}
                    disabled={!isEdit || !canWrite("contract_status.cancellation_details")}
                    data-testid="cs_cancellation_details" />
                </div>
                )}
              </CardContent>
            </Card>
    </>
  );
};

export default SaleFormBackofficeTab;
